import {
  HomeIcon,
  BuildingOffice2Icon,
  UsersIcon,
  Cog6ToothIcon,
  SparklesIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  CircleStackIcon,
  TruckIcon,
  ChatBubbleLeftRightIcon,
  BellAlertIcon,
  LifebuoyIcon,
} from "@heroicons/react/24/outline";

/**
 * Minimal Global Navigation Structure
 * - Main Operational Hub lives under "Properties"
 * - Operational modules (Gate Terminal, Parking, Visitors, Approvals, Chats, Helpdesk) live directly inside each Property
 * - Master Creations provides global catalog templates
 * - System houses global platform administration
 */
export const navigationSections = [
  {
    category: "Dashboard",
    items: [
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: HomeIcon,
        roles: [
          "admin",
          "super-admin",
          "society-admin",
          "society-manager",
          "chairman",
          "secretary",
          "treasurer",
          "guard",
          "security-head",
        ],
      },
    ],
  },
  {
    category: "Properties",
    icon: BuildingOffice2Icon,
    roles: [
      "admin",
      "super-admin",
      "society-admin",
      "society-manager",
      "chairman",
      "secretary",
      "treasurer",
    ],
    items: [
      {
        label: "All Properties",
        path: "/properties",
        icon: BuildingOffice2Icon,
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
      {
        label: "Residential",
        path: "/properties?type=RESIDENTIAL",
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
      {
        label: "Commercial",
        path: "/properties?type=COMMERCIAL",
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
      {
        label: "Industrial",
        path: "/properties?type=INDUSTRIAL",
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
      {
        label: "Hybrid",
        path: "/properties?type=HYBRID",
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
    ],
  },
  {
    category: "Users",
    icon: UsersIcon,
    roles: [
      "admin",
      "super-admin",
      "society-admin",
      "society-manager",
    ],
    items: [
      {
        label: "All Users",
        path: "/users",
        icon: UsersIcon,
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
      {
        label: "Residential",
        path: "/users?type=Residential",
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
      {
        label: "Commercial",
        path: "/users?type=Commercial",
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
      {
        label: "Industrial",
        path: "/users?type=Industrial",
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
      {
        label: "Hybrid",
        path: "/users?type=Hybrid",
        roles: ["admin", "super-admin", "society-admin", "society-manager"],
      },
    ],
  },
  {
    category: "Engagement",
    icon: ChatBubbleLeftRightIcon,
    roles: [
      "admin",
      "super-admin",
      "society-admin",
      "society-manager",
      "help-desk",
      "user",
      "resident",
    ],
    items: [
      {
        label: "Society Chats",
        path: "/society-chats",
        icon: ChatBubbleLeftRightIcon,
        roles: [
          "admin",
          "super-admin",
          "society-admin",
          "society-manager",
          "user",
          "owner",
          "tenant",
          "family-member",
          "resident",
        ],
      },
      {
        label: "Notice Broadcasts",
        path: "/send-notifications",
        icon: BellAlertIcon,
        roles: ["admin", "super-admin", "society-admin"],
        permission: "canManageNotices",
      },
      {
        label: "Support Tickets",
        path: "/support",
        icon: LifebuoyIcon,
        roles: [
          "admin",
          "super-admin",
          "society-admin",
          "society-manager",
          "help-desk",
          "user",
          "owner",
          "tenant",
          "family-member",
          "resident",
        ],
        permission: "canManageFeedback",
      },
    ],
  },
  {
    category: "Master Creations",
    icon: SparklesIcon,
    roles: [
      "admin",
      "super-admin",
      "society-admin",
      "society-manager",
    ],
    items: [
      {
        label: "Master Amenities",
        path: "/amenities",
        icon: SparklesIcon,
        permission: "canManageServiceBookings",
        roles: [
          "admin",
          "super-admin",
          "society-admin",
          "society-manager",
          "maintenance-staff",
        ],
      },
      {
        label: "Master Services",
        path: "/services",
        icon: BriefcaseIcon,
        permission: "canManageServiceBookings",
        roles: [
          "admin",
          "super-admin",
          "society-admin",
          "society-manager",
        ],
      },
      {
        label: "Delivery Companies",
        path: "/masters/delivery-companies",
        icon: TruckIcon,
        roles: ["admin", "super-admin"],
      },
      {
        label: "Cab Providers",
        path: "/masters/cab-providers",
        icon: BriefcaseIcon,
        roles: ["admin", "super-admin"],
      },
      {
        label: "Property Types",
        path: "/masters/property-types",
        icon: BuildingOffice2Icon,
        roles: ["super-admin"],
      },
      {
        label: "Master Departments",
        path: "/masters/departments",
        icon: BuildingOffice2Icon,
        roles: ["admin", "super-admin", "society-admin"],
      },
    ],
  },
  {
    category: "System",
    icon: Cog6ToothIcon,
    roles: ["admin", "super-admin"],
    items: [
      {
        label: "System Settings",
        path: "/settings",
        icon: Cog6ToothIcon,
        roles: ["admin", "super-admin"],
      },
      {
        label: "System Logs",
        path: "/system-logs",
        icon: DocumentTextIcon,
        permission: "canViewSystemLogs",
        roles: ["super-admin"],
      },
      {
        label: "Database Explorer",
        path: "/databaseviewer",
        icon: CircleStackIcon,
        roles: ["super-admin", "admin"],
      },
    ],
  },
];

// Flattens sections for backward compatibility with router or breadcrumb resolvers
export const menuItems = navigationSections.flatMap((s) => s.items);

